/** Couleurs de la charte lues depuis les jetons d'index.css (`--color-…`), pour ce qui ne passe pas par une classe :
 *  SVG dessinés en code, canvas, animations Web (`element.animate`). Une seule source de vérité : la valeur lue
 *  est celle du thème en vigueur ; les valeurs de repli ne servent qu'hors navigateur (tests) ou avant le
 *  chargement de la feuille de style. En JSX, préférer `fill="var(--color-accent)"` quand c'est possible. */

/** Jetons de couleur d'index.css (sans le préfixe `--color-`). */
export type ColorToken =
  | "ink"
  | "ink-soft"
  | "ink-muted"
  | "line"
  | "line-strong"
  | "surface"
  | "canvas"
  | "hover"
  | "selected"
  | "accent"
  | "accent-soft"
  | "brand"
  | "brand-soft"
  | "friend"
  | "friend-soft"
  | "hostile"
  | "hostile-soft"
  | "ok"
  | "ok-soft"
  | "warn"
  | "warn-soft"
  | "live"
  | "live-soft"
  | "run"
  | "run-soft"
  | "danger"
  | "danger-soft"
  | "sev-critical"
  | "sev-major"
  | "sev-moderate"
  | "sev-minor"
  | "incident-tac"
  | "incident-tac-soft"
  | "incident-tec"
  | "incident-tec-soft"
  | "incident-env"
  | "incident-env-ink"
  | "incident-env-soft"
  | "rating"
  | "mark"
  | "mark-soft"
  | "app6-friend"
  | "app6-hostile"
  | "app6-hostile-fill"
  | "flag-white"
  | "flag-fr-blue"
  | "flag-fr-red"
  | "flag-uk-blue"
  | "flag-uk-red";

/** Valeurs de repli, recopiées d'index.css (à tenir en phase ; vérifié par theme.test.ts). */
export const TOKEN_FALLBACK: Record<ColorToken, string> = {
  ink: "#1b1d21",
  "ink-soft": "#4b5160",
  "ink-muted": "#8a909c",
  line: "#e6e8ec",
  "line-strong": "#d4d7dd",
  surface: "#ffffff",
  canvas: "#f5f6f8",
  hover: "#f0f1f4",
  selected: "#eceef2",
  accent: "#2f6fed",
  "accent-soft": "#edf3fe",
  brand: "#c83e3e",
  "brand-soft": "#fbeaea",
  friend: "#1f6fb2",
  "friend-soft": "#e6f1fb",
  hostile: "#c83e3e",
  "hostile-soft": "#fbeaea",
  ok: "#15803d",
  "ok-soft": "#e7f5ec",
  warn: "#c2410c",
  "warn-soft": "#fdf0e7",
  live: "#0f766e",
  "live-soft": "#e6f4f2",
  run: "#7c3aed",
  "run-soft": "#f1ebfe",
  danger: "#c83e3e",
  "danger-soft": "#fbeaea",
  "sev-critical": "#8f1d1d",
  "sev-major": "#c83e3e",
  "sev-moderate": "#c2410c",
  "sev-minor": "#a3a3a3",
  "incident-tac": "#c83e3e",
  "incident-tac-soft": "#fbeaea",
  "incident-tec": "#6b7280",
  "incident-tec-soft": "#eef0f3",
  "incident-env": "#f2c94c",
  "incident-env-ink": "#3b2f05",
  "incident-env-soft": "#fdf6dc",
  rating: "#fbbf24",
  mark: "#f2c94c",
  "mark-soft": "#fdf6dc",
  "app6-friend": "#5cbdff",
  "app6-hostile": "#c83e3e",
  "app6-hostile-fill": "#dc7b7b",
  "flag-white": "#f1f3f5",
  "flag-fr-blue": "#5f6670",
  "flag-fr-red": "#a3a9b1",
  "flag-uk-blue": "#5f6670",
  "flag-uk-red": "#a3a9b1",
};

/** Couleur d'un jeton (`tokenColor("accent")` → « #2f6fed »), lue sur `:root` par `getComputedStyle`. */
export function tokenColor(name: ColorToken): string {
  try {
    if (typeof document !== "undefined" && typeof getComputedStyle === "function") {
      const value = getComputedStyle(document.documentElement).getPropertyValue(`--color-${name}`).trim();
      if (value) return value;
    }
  } catch {
    // Contexte sans feuille de style calculable : valeur de repli.
  }
  return TOKEN_FALLBACK[name];
}

/** Composantes « r g b » d'un jeton (`"47 111 237"`), pour une transparence : `rgb(${tokenRgb("accent")} / 0.3)`.
 *  Couleur illisible (nom de couleur, autre syntaxe) : composantes de la valeur de repli. */
export function tokenRgb(name: ColorToken): string {
  return hexToRgb(tokenColor(name)) ?? hexToRgb(TOKEN_FALLBACK[name]) ?? "0 0 0";
}

/** « #2f6fed » ou « #26e » → « 47 111 237 » ; null si la valeur n'est pas un hexadécimal. */
export function hexToRgb(hex: string): string | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const h = m[1]!.length === 3 ? [...m[1]!].map((c) => c + c).join("") : m[1]!;
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(" ");
}
